import type { Action } from '@reduxjs/toolkit'
import type { Observable } from 'rxjs'
import {
  EMPTY,
  auditTime,
  catchError,
  concat,
  concatMap,
  defer,
  endWith,
  filter,
  firstValueFrom,
  from,
  groupBy,
  ignoreElements,
  map,
  merge,
  mergeMap,
  of,
  tap,
  throwError,
  timer,
  toArray
} from 'rxjs'
import type { TRepositories } from '../../index.ts'
import type { WizardDependencies, WizardEpic } from './draftState.ts'
import type { State } from '../../Slice/index.ts'
import { orderWizardActions as wizard } from '../../Slice/OrderWizard/OrderWizardSlice.ts'
import type { DraftState, WizardItemType, WizardRemoval, WizardSampleGroup } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { FileForProcessing } from '../../../Model/Sample/ISample.ts'
import { groupsReadyForItems, heldByRemoval } from '../../../Services/OrderWizard/itemPlan.ts'
import { removablePools } from '../../../Services/OrderWizard/pools.ts'
import { planSheetApply } from '../../../Services/OrderWizard/sheet/applyPlan.ts'
import { poolLabelsDue } from '../../../Services/OrderWizard/sheet/sheetRoles.ts'
import type { ArchiveApplyEntry } from '../../../Services/OrderWizard/archiveApply.ts'
import { planArchiveApply } from '../../../Services/OrderWizard/archiveApply.ts'
import {
  archiveConversionsDue,
  dueForAttach,
  dueForConversion,
  dueForMove,
  settingsWriteDue
} from '../../../Services/OrderWizard/writeState.ts'
import { determineSampleOperations } from '../../../View/Page/HomePage/Modals/DetermineSampleOperations.ts'
import { isOpCreateMetadata, stringifyError } from '../../../View/Page/HomePage/Modals/CSVModalTypes.ts'
import type { MergedOperation } from '../../../View/Page/HomePage/Modals/CSVModalTypes.ts'
import { mapToBatches } from '../../../View/Page/HomePage/Modals/MapToBatches.ts'
import { handleBatchUpdateSamples } from '../Common/handleBatchUpdateSamples.ts'
import { messageOf } from './messages.ts'
import { RETRY_DELAYS_MS, isTransient, retryTransient } from './retryTransient.ts'
import type { LocatedItem } from '../../../Services/OrderWizard/removal.ts'
import { pendingRemovals, removalTargets } from '../../../Services/OrderWizard/removal.ts'
import { queued } from './serially.ts'
import { isSubmitRequest, submitStep } from './OrderWizardSubmit.ts'
import { orderHasSettings, regionFor } from '../../../Services/OrderWizard/orderSettingsPlan.ts'
import { describeOrderWriteRefusal } from '../Order/orderWriteRefusal.ts'
import { describeAttachRefusal } from '../Order/AttachReverseFile/attachRefusal.ts'
import { SampleConfigurationTypes } from '../../../Generated/SampleConfigurationType.ts'
import { draftIdOf } from './draftIdOf.ts'

type RootState = typeof State
type Repository = TRepositories['OrderRepository']

type WriteContext = {
  draftId: string
  state: () => Promise<RootState>
  repository: Repository
  pools: WizardDependencies['PoolUploadRepository']
  delaysMs: readonly number[]
}

const retryIn = <T>(context: WriteContext, options: { conflicts?: boolean } = {}) =>
  retryTransient<T>({ ...options, delaysMs: context.delaysMs })

/** The step's work planned against the draft as it is when the step starts, not when the pass was asked for. */
const withDraft = (context: WriteContext, plan: (draft: DraftState, state: RootState) => Observable<Action>): Observable<Action> =>
  defer(() => context.state()).pipe(
    mergeMap((state) => {
      const draft = state.orderWizard.drafts[context.draftId]
      return draft ? plan(draft, state) : EMPTY
    })
  )

const ITEM_CREATION_CONCURRENCY = 4
export const PASS_DEBOUNCE_MS = 300

/* the HTTP layer hands back whatever body came, so an empty one would otherwise pass as "nothing changed" */
const itemsOf = <T>(items: T[] | null | undefined, what: string): T[] => {
  if (!Array.isArray(items)) {
    throw new Error(`${what}: the server sent no items back`)
  }
  return items
}

const itemsOfOrder = async (context: WriteContext, orderId: string) => {
  const pools = await firstValueFrom(context.repository.getAllSamplePools({ orderId }).pipe(retryIn(context)))
  return itemsOf(pools, 'Reading the order').flatMap((pool): FileForProcessing[] => [
    ...(pool.samples ?? []),
    ...(pool.negativeControls ?? [])
  ])
}

const readOrder = async (context: WriteContext, orderId: string) => {
  const { repository } = context
  const [order, items, metadatas] = await Promise.all([
    firstValueFrom(repository.getOrder(orderId).pipe(retryIn(context))),
    itemsOfOrder(context, orderId),
    firstValueFrom(repository.getSampleMetaDatas(orderId).pipe(retryIn(context)))
  ])
  return { order, items, metadatas }
}

/**
 * Creates an item, repeating after a transient failure only once the order shows no item for the file:
 * a 5xx can arrive after the server made it.
 */
const createItemOnce = (
  context: WriteContext,
  orderId: string,
  poolId: string,
  payload: Parameters<Repository['setContent']>[2],
  attempt = 0
): Observable<{ id: string; itemType?: WizardItemType }> =>
  context.repository.setContent(orderId, poolId, payload).pipe(
    catchError((error: unknown) => {
      if (!isTransient(error) || attempt >= context.delaysMs.length) {
        return throwError(() => error)
      }
      return timer(context.delaysMs[attempt]).pipe(
        mergeMap(() => from(itemsOfOrder(context, orderId))),
        mergeMap((items) => {
          const made = items.find((item) => item.forwardFileId === payload.forwardFileId)
          return made ? of(made) : createItemOnce(context, orderId, poolId, payload, attempt + 1)
        })
      )
    })
  )

/**
 * A pool item per group whose files are all uploaded, the way the classic upload page creates them. Each
 * waits for a free slot, so it looks again first: a group removed meanwhile, or whose files an item being
 * removed holds again, is not created.
 */
const createItems = (context: WriteContext): Observable<Action> =>
  withDraft(context, (current) => {
    const { draftId } = context
    const orderId = current.orderId
    const poolId = current.poolIds[0]
    if (!orderId || !poolId) {
      return EMPTY
    }
    return from(groupsReadyForItems(current)).pipe(
      mergeMap(
        ({ group, payload }) =>
          withDraft(context, (latest) =>
            latest.groups[group.key]?.item.status !== 'waiting' || heldByRemoval(latest, payload)
              ? EMPTY
              : concat(
                  of(wizard.itemCreationStarted({ draftId, groupKey: group.key })),
                  createItemOnce(context, orderId, poolId, payload).pipe(
                    map((item) => {
                      if (!item?.id) {
                        throw new Error('the server sent no item back')
                      }
                      return wizard.itemCreated({ draftId, groupKey: group.key, itemId: item.id, itemType: item.itemType, poolId })
                    }),
                    catchError((error: unknown) => of(wizard.itemCreationFailed({ draftId, groupKey: group.key, error: messageOf(error) })))
                  )
                )
          ),
        ITEM_CREATION_CONCURRENCY
      )
    )
  })

const statusOf = (error: unknown) => (error as { status?: unknown } | null)?.status

/* a repeat after a lost answer finds the row deleted already, which is what was asked for */
const goneIsDone = catchError((error: unknown) => (statusOf(error) === 404 ? EMPTY : throwError(() => error)))

/** The classic view's sample delete: a soft delete carrying the item's current version. */
const deleteItem = (context: WriteContext, orderId: string, { item, poolId }: LocatedItem) => {
  const { repository } = context
  return defer(() =>
    repository
      .fetchPoolItem({ orderId, poolId, itemId: item.id })
      .pipe(
        mergeMap((stored) =>
          stored ? repository.deletePoolItem({ orderId, poolId, itemId: item.id, version: stored.version ?? '' }) : EMPTY
        )
      )
  ).pipe(retryIn(context, { conflicts: true }), goneIsDone)
}

/**
 * One removed group out of the order: first the metadata bound to its items, through the classic "Delete
 * Metadata", then the items. A sample delete is soft, so metadata deleted after it could be left bound to
 * the dead item if that second step failed; this way a failure leaves at worst a live item to retry.
 */
const removeOne = (
  context: WriteContext,
  orderId: string,
  removal: WizardRemoval,
  targets: LocatedItem[],
  metadatas: { id: string; sampleId?: string | null }[]
): Observable<Action> => {
  const { draftId, repository } = context
  const itemIds = new Set(targets.map(({ item }) => item.id))
  const bound = metadatas.filter((metadata) => !!metadata.sampleId && itemIds.has(metadata.sampleId))
  return concat(
    from(bound).pipe(
      concatMap((metadata) => repository.deleteMetadata({ orderId, metadataId: metadata.id }).pipe(retryIn(context), goneIsDone))
    ),
    from(targets).pipe(concatMap((target) => deleteItem(context, orderId, target)))
  ).pipe(
    ignoreElements(),
    endWith(wizard.itemRemovalCompleted({ draftId, groupKey: removal.groupKey })),
    catchError((error: unknown) => of(wizard.itemRemovalFailed({ draftId, groupKeys: [removal.groupKey], error: messageOf(error) })))
  )
}

/**
 * Removed groups out of the order. Running in the queue, it comes after any write already under way for
 * them, so it sees the metadata a sheet write bound and the pool a move put the item in: both are read
 * from the server here rather than taken from the draft.
 */
const removeItems = (context: WriteContext): Observable<Action> =>
  withDraft(context, (current) => {
    const { draftId, repository } = context
    const orderId = current.orderId
    const due = pendingRemovals(current)
    if (!orderId || due.length === 0) {
      return EMPTY
    }
    return defer(async () => {
      const [pools, metadatas] = await Promise.all([
        firstValueFrom(repository.getAllSamplePools({ orderId }).pipe(retryIn(context))),
        firstValueFrom(repository.getSampleMetaDatas(orderId).pipe(retryIn(context)))
      ])
      const located = itemsOf(pools, 'Reading the order').flatMap((pool): LocatedItem[] =>
        [...(pool.samples ?? []), ...(pool.negativeControls ?? [])].map((item) => ({ item, poolId: pool.id }))
      )
      return { located, metadatas: metadatas ?? [] }
    }).pipe(
      mergeMap(({ located, metadatas }) =>
        from(due).pipe(
          mergeMap(
            (removal) => removeOne(context, orderId, removal, removalTargets(removal, located, current), metadatas),
            ITEM_CREATION_CONCURRENCY
          )
        )
      ),
      catchError((error: unknown) =>
        of(
          wizard.itemRemovalFailed({
            draftId,
            groupKeys: due.map((removal) => removal.groupKey),
            error: messageOf(error)
          })
        )
      )
    )
  })

/** The classic view's conversion: the item as the server holds it now, only its type flipped. */
const convertItem = (context: WriteContext, orderId: string, poolId: string, itemId: string, itemType: WizardItemType) =>
  defer(() =>
    context.repository
      .fetchPoolItem({ orderId, poolId, itemId })
      .pipe(mergeMap((item) => context.repository.updatePoolContent(orderId, poolId, [{ ...item, itemType }])))
  ).pipe(
    retryIn(context, { conflicts: true }),
    map((items) => itemsOf(items, 'Converting').find((item) => item?.id === itemId)?.itemType === itemType)
  )

/** An item whose label changed after it was created is converted. */
const convertItems = (context: WriteContext): Observable<Action> =>
  withDraft(context, (current) => {
    const { draftId } = context
    const orderId = current.orderId
    if (!orderId) {
      return EMPTY
    }
    const due = Object.values(current.groups).filter(dueForConversion)
    return from(due).pipe(
      concatMap((group) => {
        const target = group.itemType as WizardItemType
        return convertItem(context, orderId, group.item.poolId ?? current.poolIds[0], group.item.itemId as string, target).pipe(
          map((converted) =>
            converted
              ? wizard.itemConverted({ draftId, groupKey: group.key, itemType: target })
              : wizard.itemActionFailed({ draftId, groupKeys: [group.key], error: 'The item could not be converted' })
          ),
          catchError((error: unknown) =>
            of(wizard.itemActionFailed({ draftId, groupKeys: [group.key], error: `Converting failed: ${messageOf(error)}` }))
          )
        )
      })
    )
  })

/**
 * Items assigned to a later pool move there once created in the first, with the classic view's move
 * between pools; each later pool is created when first needed, or taken over when an earlier creation
 * made it without the answer arriving. Pools never come from sequencing runs.
 */
const movePools = (context: WriteContext): Observable<Action> =>
  withDraft(context, (current, state) => {
    const { draftId, repository } = context
    const user = state.authentication.user
    const orderId = current.orderId
    if (!orderId || !current.poolIds[0]) {
      return EMPTY
    }
    const misplaced = Object.values(current.groups).filter((group) => dueForMove(current, group))
    if (misplaced.length === 0) {
      return EMPTY
    }
    const targets = [...new Set(misplaced.map((group) => group.pool ?? 0))].sort((a, b) => a - b)
    const poolIds = [...current.poolIds]
    const missing = targets.filter((pool) => !poolIds[pool])
    /* a pool creation whose answer was lost left an empty pool the draft never recorded: it is taken rather than a second one made */
    const ensurePools$ =
      missing.length === 0
        ? EMPTY
        : defer(async () => {
            const pools = itemsOf(
              await firstValueFrom(repository.getAllSamplePools({ orderId }).pipe(retryIn(context))),
              'Reading the pools'
            )
            return pools
              .filter(
                (pool) =>
                  !current.poolIds.includes(pool.id) &&
                  !(current.stuckPools ?? []).includes(pool.id) &&
                  (pool.samples ?? []).length + (pool.negativeControls ?? []).length === 0
              )
              .map((pool) => pool.id)
          }).pipe(
            mergeMap((unrecorded) =>
              from(missing).pipe(
                concatMap((pool) => {
                  const taken = unrecorded.shift()
                  const pool$ = taken ? of({ id: taken }) : (repository.createSamplePool({ orderId }, user) as Observable<{ id: string }>)
                  return pool$.pipe(
                    tap((created) => {
                      poolIds[pool] = created.id
                    }),
                    map((created) => wizard.poolCreatedAt({ draftId, pool, poolId: created.id }))
                  )
                })
              )
            )
          )
    const move$ = defer(() => {
      const batches = new Map<string, { source: string; target: string; groups: WizardSampleGroup[] }>()
      for (const group of misplaced) {
        const source = group.item.poolId ?? poolIds[0]
        const target = poolIds[group.pool ?? 0]
        const key = `${source}>${target}`
        const batch = batches.get(key) ?? { source, target, groups: [] }
        batch.groups.push(group)
        batches.set(key, batch)
      }
      return from(batches.values()).pipe(
        concatMap((batch) =>
          repository
            .movePoolItems({
              orderId,
              sourcePoolId: batch.source,
              targetPoolId: batch.target,
              items: batch.groups.map((group) => group.item.itemId as string)
            })
            .pipe(
              retryIn(context),
              map(() => wizard.itemMoved({ draftId, groupKeys: batch.groups.map((group) => group.key), poolId: batch.target })),
              catchError((error: unknown) =>
                of(
                  wizard.itemActionFailed({
                    draftId,
                    groupKeys: batch.groups.map((group) => group.key),
                    error: `Moving to the pool failed: ${messageOf(error)}`
                  })
                )
              )
            )
        )
      )
    })
    return concat(ensurePools$, move$).pipe(
      catchError((error: unknown) =>
        of(
          wizard.itemActionFailed({
            draftId,
            groupKeys: misplaced.map((group) => group.key),
            error: `Creating the pool failed: ${messageOf(error)}`
          })
        )
      )
    )
  })

/**
 * The sheet's pool names onto the pools, as the classic view's rename sets them, never over a label the
 * wizard did not write. Cosmetic: a failure is logged and the next pass tries again.
 */
const labelPools = (context: WriteContext): Observable<Action> =>
  withDraft(context, (current, state) => {
    const { draftId, repository } = context
    const orderId = current.orderId
    const due = poolLabelsDue(current)
    if (!orderId || due.length === 0) {
      return EMPTY
    }
    const user = state.authentication.user
    return defer(async () =>
      itemsOf(await firstValueFrom(repository.getAllSamplePools({ orderId }).pipe(retryIn(context))), 'Reading the pools')
    ).pipe(
      mergeMap((pools) =>
        from(due).pipe(
          concatMap(({ poolId, label }) => {
            const pool = pools.find((candidate) => candidate.id === poolId)
            const labelled = wizard.poolLabelled({ draftId, poolId, label: label ?? '' })
            if (!pool?.version) {
              return EMPTY
            }
            const onServer = pool.label ?? ''
            /* a label the lab set itself, here or in the classic view, beats the sheet's */
            if (onServer === (label ?? '') || (onServer !== '' && onServer !== current.poolLabels?.[poolId])) {
              return of(labelled)
            }
            return repository.updateSamplePoolLabel({ orderId, poolId, label, version: pool.version }, user).pipe(
              retryIn(context),
              map(() => labelled)
            )
          })
        )
      ),
      catchError((error: unknown) => {
        console.warn('Order wizard: naming a pool failed', error)
        return EMPTY
      })
    )
  })

/** A refusal by the server, not a failure to get its answer: repeating the request would be refused again. */
const refusedByServer = (error: unknown) => !isTransient(error)

/**
 * Pools left empty are deleted as the classic view deletes a pool, but only once the server confirms they
 * hold no item and no uploaded file, since the delete takes a pool's contents with it. A refused delete is
 * left to the lab; one that got no answer is tried again next pass.
 */
const removeEmptyPools = (context: WriteContext): Observable<Action> =>
  withDraft(context, (current, state) => {
    const { draftId, repository } = context
    const orderId = current.orderId
    const candidates = removablePools(current)
    if (!orderId || candidates.length === 0) {
      return EMPTY
    }
    const removed = (poolId: string) => wizard.poolRemoved({ draftId, poolId })
    const refused = (poolId: string) => wizard.poolRemovalRefused({ draftId, poolId })
    const deletePool = (poolId: string) =>
      repository.deleteSamplePool({ orderId, poolId }, state.authentication.user).pipe(
        retryIn(context),
        ignoreElements(),
        endWith(removed(poolId)),
        catchError((error: unknown) => (statusOf(error) === 404 ? of(removed(poolId)) : throwError(() => error)))
      )
    return defer(async () => {
      const pools = await firstValueFrom(repository.getAllSamplePools({ orderId }).pipe(retryIn(context)))
      return itemsOf(pools, 'Reading the pools')
    }).pipe(
      mergeMap((pools) =>
        from(candidates).pipe(
          concatMap((poolId) => {
            const pool = pools.find((candidate) => candidate.id === poolId)
            if (!pool) {
              return of(removed(poolId))
            }
            if ((pool.samples ?? []).length + (pool.negativeControls ?? []).length > 0) {
              return of(refused(poolId))
            }
            return defer(() => context.pools.completedPoolFiles(orderId, poolId)).pipe(
              retryIn(context),
              mergeMap((files) => (files.length > 0 ? of(refused(poolId)) : deletePool(poolId))),
              catchError((error: unknown) => {
                console.warn('Order wizard: an empty pool could not be removed', error)
                return refusedByServer(error) ? of(refused(poolId)) : EMPTY
              })
            )
          })
        )
      ),
      catchError((error: unknown) => {
        console.warn('Order wizard: empty pools could not be checked', error)
        return EMPTY
      })
    )
  })

/* a CreateMetadata for a row that brought nothing but estimated primers would add an empty metadata record */
const bringsMetadata = (op: MergedOperation) =>
  !isOpCreateMetadata(op) ||
  !!op.metadata.customerSampleName?.trim() ||
  Object.keys(op.metadata).some((key) => !['customerSampleName', 'sampleId'].includes(key))

const writeBatches = (batches: ReturnType<typeof mapToBatches>, repository: Repository) =>
  from(batches).pipe(
    concatMap((batch) => handleBatchUpdateSamples(batch, repository)),
    map((result) => {
      itemsOf(result?.samples, 'Writing the sheet data')
      return result
    })
  )

/**
 * Sample names, exact primers and every mapped metadata column into the created items, through the
 * classic import's own planning (`determineSampleOperations`) and writing (`handleBatchUpdateSamples`),
 * so the result is what the classic sheet import would have written.
 */
const applySheet = (context: WriteContext): Observable<Action> =>
  withDraft(context, (current) => {
    const { draftId, repository } = context
    const orderId = current.orderId
    const entries = planSheetApply(current)
    if (!orderId || entries.length === 0) {
      return EMPTY
    }
    const keys = entries.map((entry) => entry.group.key)
    const write$ = defer(async () => {
      const { order, items, metadatas } = await readOrder(context, orderId)
      const plan = (rows: typeof entries) =>
        determineSampleOperations(
          items,
          metadatas,
          rows.map((entry) => entry.result),
          { overrideCustomerPrimer: true, overrideCustomerData: true },
          'CREATE_SAMPLES',
          order
        )
      let accepted = entries
      let planned = plan(entries)
      const refused: { key: string; error: string }[] = []
      /* a row the import refuses is failed alone; the others are written */
      if (planned.errors.length > 0) {
        accepted = entries.filter((entry) => {
          const errors = plan([entry]).errors
          if (errors.length > 0) {
            refused.push({ key: entry.group.key, error: `the import refused the row: ${errors.map(stringifyError).join('; ')}` })
          }
          return errors.length === 0
        })
        planned = plan(accepted)
        if (planned.errors.length > 0) {
          throw new Error(`the import refused the rows (${planned.errors.map(stringifyError).join('; ')})`)
        }
      }
      return { batches: mapToBatches({ ...planned, result: planned.result.filter(bringsMetadata) }), refused }
    }).pipe(
      mergeMap(({ batches, refused }) =>
        concat(
          writeBatches(batches, repository).pipe(ignoreElements()),
          from(refused).pipe(
            map(({ key, error }) => wizard.sheetApplyFinished({ draftId, keys: [key], error: `Writing the sheet data failed: ${error}` }))
          ),
          of(wizard.sheetApplyFinished({ draftId, keys: keys.filter((key) => !refused.some((entry) => entry.key === key)) }))
        )
      )
    )
    return concat(
      of(
        wizard.sheetApplyStarted({
          draftId,
          groups: entries.map((entry) => ({
            key: entry.group.key,
            primerSource: entry.primerSource,
            sheetKey: entry.sheetKey,
            primers: entry.primers,
            itemType: entry.itemType,
            fields: entry.fields
          }))
        })
      ),
      write$
    ).pipe(
      catchError((error: unknown) =>
        of(wizard.sheetApplyFinished({ draftId, keys, error: `Writing the sheet data failed: ${messageOf(error)}` }))
      )
    )
  })

const archiveFileId = (items: FileForProcessing[], itemId: string) => items.find((item) => item.id === itemId)?.forwardFileId

/**
 * An archive's sheet samples through the classic import: all its rows keyed to its item in one batch, so
 * the first row updates the item and every further one makes a new item on the same files. The new items
 * are matched to rows by their order among the items the order did not hold before: the client sends one
 * POST per row, in row order, and the server answers with no sample name to match by.
 */
const writeArchive = (context: WriteContext, entry: ArchiveApplyEntry, orderId: string): Observable<Action> => {
  const { draftId, repository } = context
  const write$ = defer(async () => {
    const { order, items, metadatas } = await readOrder(context, orderId)
    const fileId = archiveFileId(items, entry.itemId)
    /* a split that failed halfway left items the draft never recorded; splitting again would duplicate them */
    if (entry.initial && fileId && items.some((item) => item.id !== entry.itemId && item.forwardFileId === fileId)) {
      throw new Error('the archive is already partly split; finish it in the classic view')
    }
    const planned = determineSampleOperations(
      items,
      metadatas,
      entry.results,
      { overrideCustomerPrimer: true, overrideCustomerData: true },
      'CREATE_SAMPLES',
      order
    )
    if (planned.errors.length > 0) {
      throw new Error(`the import refused the rows (${planned.errors.map((e) => e.type).join(', ')})`)
    }
    return { batches: mapToBatches(planned), existingIds: new Set(items.map((item) => item.id)) }
  }).pipe(
    mergeMap(({ batches, existingIds }) =>
      writeBatches(batches, repository).pipe(
        toArray(),
        map((results) => {
          const created = results.flatMap((result) => result.samples).filter((item) => item && !existingIds.has(item.id))
          /* an answer naming an item the order held already (a deduplicated create) would shift every later row onto the wrong item */
          if (entry.initial && created.length !== entry.samples.length - 1) {
            throw new Error(`the server made ${created.length} items for ${entry.samples.length - 1} further samples`)
          }
          const recorded: Record<string, { itemId: string; itemType?: WizardItemType }> = {}
          if (entry.initial) {
            entry.samples.forEach((sample, index) => {
              const item = index === 0 ? { id: entry.itemId, itemType: entry.group.item.itemType ?? 'SAMPLE' } : created[index - 1]
              if (item) {
                recorded[sample.key] = { itemId: item.id, itemType: item.itemType }
              }
            })
          }
          return wizard.archiveItemsRecorded({ draftId, groupKey: entry.group.key, items: recorded })
        })
      )
    )
  )
  const unwritable =
    entry.unwritable.length > 0
      ? `; not added (the archive is already split, add them in the classic view): ${entry.unwritable.join(', ')}`
      : ''
  return concat(
    of(wizard.sheetApplyStarted({ draftId, groups: [{ key: entry.group.key, sheetKey: entry.sheetKey }] })),
    write$,
    of(
      wizard.sheetApplyFinished({
        draftId,
        keys: [entry.group.key],
        error: unwritable ? `Some sheet samples were not written${unwritable}` : undefined
      })
    )
  ).pipe(
    catchError((error: unknown) =>
      of(
        wizard.sheetApplyFinished({ draftId, keys: [entry.group.key], error: `Writing the archive's samples failed: ${messageOf(error)}` })
      )
    )
  )
}

const applyArchives = (context: WriteContext): Observable<Action> =>
  withDraft(context, (current) => {
    const orderId = current.orderId
    const entries = planArchiveApply(current)
    if (!orderId || entries.length === 0) {
      return EMPTY
    }
    return from(entries).pipe(concatMap((entry) => writeArchive(context, entry, orderId)))
  })

/** Archive samples labelled as controls (or back) are converted as the classic view converts an item. */
const convertArchiveItems = (context: WriteContext): Observable<Action> =>
  withDraft(context, (current) => {
    const { draftId } = context
    const orderId = current.orderId
    if (!orderId) {
      return EMPTY
    }
    return from(archiveConversionsDue(current)).pipe(
      concatMap(({ group, key, itemId, itemType }) =>
        convertItem(context, orderId, group.item.poolId ?? current.poolIds[0], itemId, itemType).pipe(
          map((converted) =>
            converted
              ? wizard.archiveItemsRecorded({ draftId, groupKey: group.key, items: { [key]: { itemId, itemType } } })
              : wizard.itemActionFailed({ draftId, groupKeys: [group.key], error: `Converting ${key} failed` })
          ),
          catchError((error: unknown) =>
            of(wizard.itemActionFailed({ draftId, groupKeys: [group.key], error: `Converting ${key} failed: ${messageOf(error)}` }))
          )
        )
      )
    )
  })

/* the order refuses merged reads while a sample holds an R2: the samples the lab removed for it go first */
const waitsForRemovals = (draft: DraftState) => {
  const format = draft.settingsWrite?.sampleConfiguration
  return !!format && SampleConfigurationTypes[format].impliesMerged && pendingRemovals(draft).length > 0
}

/**
 * Settings the lab changed into the order, through the classic order update: the order as it holds it
 * now, read immediately before, with only target, processing type and singletons from the draft.
 */
const writeSettings = (context: WriteContext): Observable<Action> =>
  withDraft(context, (current) => {
    const { draftId, repository } = context
    const { orderId, settings, settingsWrite } = current
    if (!orderId || !settings || !settingsWrite || !settingsWriteDue(current) || waitsForRemovals(current)) {
      return EMPTY
    }
    const { revision } = settingsWrite
    return defer(() =>
      repository.getOrder(orderId).pipe(
        mergeMap((order) =>
          orderHasSettings(order, settings, settingsWrite.sampleConfiguration)
            ? of(order)
            : repository.updateOrderName({
                orderId,
                version: order.version ?? '0',
                name: order.name,
                description: order.description,
                taxa: settings.targetTaxonomy.value,
                targetRegion: regionFor(order, settings),
                allowSingletons: settings.allowSingletons,
                processingType: settings.processingType.value,
                redlistMode: order.redlistMode ?? 'NONE',
                sampleConfiguration: settingsWrite.sampleConfiguration ?? order.sampleConfiguration
              })
        )
      )
    ).pipe(
      retryIn(context, { conflicts: true }),
      map(() => wizard.settingsWritten({ draftId, revision })),
      catchError((error: unknown) =>
        of(
          wizard.settingsWriteFailed({
            draftId,
            revision,
            error: describeOrderWriteRefusal(error) ?? `Saving the order settings failed: ${messageOf(error)}`
          })
        )
      )
    )
  })

/** Thrown before the attach request, so the refusal is named and not retried as a version conflict. */
class AttachBlocked extends Error {}

/** Why the freshly read item can never take [reverseFileId]; the endpoint answers these with a bare 409. */
const attachBlockedBy = (stored: { isMerged?: boolean; forwardFileId?: string; reverseFileId?: string }, reverseFileId: string) => {
  if (stored.isMerged) {
    return 'The sample is still merged — switch the order to paired-end reads first.'
  }
  if (stored.forwardFileId === reverseFileId) {
    return 'That file is the sample’s own R1 file.'
  }
  if (stored.reverseFileId) {
    return 'The sample already has a different R2 file.'
  }
  return undefined
}

/**
 * R2 files that joined created items (the order became paired-end) set through the attach op, which also
 * deletes an item the R2 had become on its own. The item is read first: an attach whose answer was lost
 * shows there as done.
 */
const attachReverseFiles = (context: WriteContext): Observable<Action> =>
  withDraft(context, (current) => {
    const { draftId, repository } = context
    const orderId = current.orderId
    if (!orderId) {
      return EMPTY
    }
    return from(Object.values(current.groups).filter((group) => dueForAttach(current, group))).pipe(
      concatMap((group) => {
        const itemId = group.item.itemId as string
        const poolId = group.item.poolId ?? current.poolIds[0]
        const reverseFileId = current.files[group.reverse as string]?.upload?.serverFileId as string
        const absorbSampleId = group.reverseAttach?.absorbItemId
        return defer(() =>
          repository.fetchPoolItem({ orderId, poolId, itemId }).pipe(
            mergeMap((stored) => {
              if (stored?.reverseFileId === reverseFileId) {
                return of(stored)
              }
              const blocked = stored && attachBlockedBy(stored, reverseFileId)
              return blocked
                ? throwError(() => new AttachBlocked(blocked))
                : repository.attachReverseFile({
                    orderId,
                    poolId,
                    sampleId: itemId,
                    request: { version: stored?.version ?? '', reverseFileId, ...(absorbSampleId ? { absorbSampleId } : {}) }
                  })
            })
          )
        ).pipe(
          retryIn(context, { conflicts: true }),
          map(() => wizard.reverseFileAttached({ draftId, groupKey: group.key })),
          catchError((error: unknown) =>
            of(
              wizard.reverseFileAttachFailed({
                draftId,
                groupKey: group.key,
                error: error instanceof AttachBlocked ? error.message : describeAttachRefusal(error)
              })
            )
          )
        )
      })
    )
  })

const NOT_FOUND = new Set([404, 410])

/** The repository with its order reads counted when they find no order. */
const watchedForGone = (repository: Repository, gone: { reads: number }): Repository => {
  const counted = <T>(read$: Observable<T>) =>
    read$.pipe(
      tap({
        error: (error: unknown) => {
          if (NOT_FOUND.has(statusOf(error) as number)) {
            gone.reads++
          }
        }
      })
    )
  return {
    ...repository,
    getOrder: (...args: Parameters<Repository['getOrder']>) => counted(repository.getOrder(...args)),
    getAllSamplePools: (...args: Parameters<Repository['getAllSamplePools']>) => counted(repository.getAllSamplePools(...args))
  }
}

/**
 * After a pass whose reads found no order more than once: once the order is confirmed gone, the draft is
 * dropped with that reason rather than every item showing its own failure.
 */
const orderGone = (context: WriteContext, repository: Repository, gone: { reads: number }): Observable<Action> =>
  defer(() => {
    if (gone.reads < 2) {
      return EMPTY
    }
    return withDraft(context, (draft) => {
      const orderId = draft.orderId
      if (!orderId || draft.submission) {
        return EMPTY
      }
      const drop = of(wizard.draftDiscarded({ draftId: draft.id, reason: 'order-missing' }))
      return repository.getOrder(orderId).pipe(
        mergeMap((order) => (order?.deletedAt ? drop : EMPTY)),
        catchError((error: unknown) => (NOT_FOUND.has(statusOf(error) as number) ? drop : EMPTY))
      )
    })
  })

/**
 * In this order, each step reading the draft as the previous ones left it. Removals come first: a switch to
 * merged reads waits for them, and so does an item on files the removed one held (a re-added sample).
 */
const WRITE_STEPS = [
  removeItems,
  writeSettings,
  createItems,
  attachReverseFiles,
  convertItems,
  movePools,
  labelPools,
  removeEmptyPools,
  applySheet,
  applyArchives,
  convertArchiveItems
]

const TRIGGERS = [
  wizard.uploadSucceeded,
  wizard.poolCreated,
  wizard.draftReconciled,
  wizard.detectionCompleted,
  wizard.itemTypeToggled,
  wizard.sheetLoaded,
  wizard.sheetMappingChanged,
  wizard.sheetRemoved,
  wizard.sheetRestored,
  wizard.estimatesAdded,
  wizard.questionAnswered,
  wizard.primersEdited,
  wizard.archiveTypeToggled,
  wizard.groupRetryRequested,
  wizard.itemsRemoved,
  wizard.itemsReadded,
  wizard.removalRetryRequested,
  wizard.settingChanged,
  wizard.settingsRetryRequested,
  wizard.reversePairingRequested
]

/* `flush`: a pass that has to run before the request queued with it rather than wait out the debounce */
type QueueEntry = { kind: 'pass'; draftId: string; flush?: boolean } | { kind: 'submit'; draftId: string; action: Action }

const entriesOf = (action: Action): QueueEntry[] => {
  const draftId = draftIdOf(action)
  if (!draftId) {
    return []
  }
  if (isSubmitRequest(action)) {
    /* a pass first, so whatever the draft still owes the order is written before the order is read for submit */
    return [
      { kind: 'pass', draftId, flush: true },
      { kind: 'submit', draftId, action }
    ]
  }
  return TRIGGERS.some((trigger) => trigger.match(action)) ? [{ kind: 'pass', draftId }] : []
}

/**
 * Every write into an order, one pass at a time per draft: the classic endpoints carry item and order
 * versions, so racing writes would refuse or overwrite each other. Review and submit go through the same
 * queue, so no pass writes while the order is read for submit.
 */
export const createOrderWritesEpic =
  ({ delaysMs = RETRY_DELAYS_MS }: { delaysMs?: readonly number[] } = {}): WizardEpic =>
  (action$, state$, { OrderRepository, PoolUploadRepository, OrderSubmissionRepository }) =>
    action$.pipe(
      mergeMap((action) => from(entriesOf(action))),
      groupBy((entry) => entry.draftId),
      mergeMap((perDraft) => {
        const state = () => firstValueFrom(state$)
        const pass = (draftId: string) => {
          const gone = { reads: 0 }
          const context: WriteContext = { draftId, state, repository: watchedForGone(OrderRepository, gone), pools: PoolUploadRepository, delaysMs }
          return concat(...WRITE_STEPS.map((step) => step(context)), orderGone(context, OrderRepository, gone)).pipe(
            catchError((error: unknown) => {
              console.error('Order wizard: a write pass failed', error)
              return EMPTY
            })
          )
        }
        return merge(
          perDraft.pipe(
            filter((entry) => entry.kind === 'pass' && !entry.flush),
            auditTime(PASS_DEBOUNCE_MS)
          ),
          perDraft.pipe(filter((entry) => entry.kind === 'submit' || !!entry.flush))
        ).pipe(
          queued(
            (entry: QueueEntry) =>
              entry.kind === 'pass'
                ? pass(entry.draftId)
                : submitStep(entry.action, {
                    draftId: entry.draftId,
                    state,
                    repository: OrderRepository,
                    submissions: OrderSubmissionRepository
                  }),
            (entry) => entry.kind === 'pass'
          )
        )
      })
    )

export const orderWritesEpic: WizardEpic = createOrderWritesEpic()
